import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import { Menu, MenuItem } from '@mui/material';
import L from 'leaflet';
import { useRef, useState, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import type { Position, PointItem } from '../../types.js';
import { MapEvents } from './MapEvents.js';
import { MapViewController } from './MapViewController.js';
import { AdaptiveRouteLayer } from './AdaptiveRouteLayer.js';
import { SurfaceHighlightLayer } from './SurfaceHighlightLayer.js';
import { ClimbHighlightLayer } from './ClimbHighlightLayer.js';
import { MapControls } from './mapControls/MapControls.js';
import MapControlsRoute from './mapControls/MapControlsRoute.js';
import ViewEditModeToggle, { type ViewEditMode, type ViewEditModeToggleRef } from './ViewEditModeToggle.js';
import { getDetailPanelHeightForMapShift } from '../../utils/detailPanelUtils.js';
import { Link as RouterLink } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { hapticMedium } from '../../utils/haptics.js';
import 'leaflet/dist/leaflet.css';
import './CyclingMap.css';

// Fix for Leaflet's default marker icon issue with bundlers
// This prevents the broken image box around markers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: undefined,
  iconUrl: undefined,
  shadowUrl: undefined,
});

interface CyclingMapProps {
  points: PointItem[];
  route: Position[];
  isLoading: boolean;
  hasRoutingError?: boolean; // Whether the last routing attempt failed
  isSavingRoute?: boolean; // Whether route is being saved (including thumbnail generation)
  onMapClick: (position: Position, pointType?: 'via' | 'end', fromSearch?: boolean) => void;
  onMarkerClick: (id: string) => void;
  onMarkerDrag: (id: string, position: Position) => void;
  activePoint: number | null;
  onHover: (point: number | null) => void;
  detailPanelOpen: boolean;
  onFitMapToRoute?: (fitFunction: (points: Array<{ lat: number; lon: number }>) => void) => void;
  onAddRoutePointViaSearch?: (position: Position, pointType?: 'via' | 'end') => void;
  onManualFitToRoute?: () => void; // Function to manually fit to route
  onDeleteRoute?: () => void; // Function to delete the current route
  onUndo?: () => void; // Function to undo last change
  onRedo?: () => void; // Function to redo last undone change
  onReverseRoute?: () => void; // Function to reverse the route direction
  onDownload?: () => void; // Function to download/save the route
  onGpxDownload?: () => void; // Function to download the route as GPX file
  onResetRoute?: () => void; // Function to reset route to saved version
  onOverwriteSave?: () => void; // Function to overwrite the existing saved route
  canUndo?: boolean; // Whether undo is available
  canRedo?: boolean; // Whether redo is available
  hasRoutePoints?: boolean; // Whether any route points exist
  routePointsCount?: number; // Number of route points
  hasLoadedRoute?: boolean; // Whether a route has been loaded from storage
  hasUnsavedChanges?: boolean; // Whether the route has unsaved changes
  currentRouteOwnerId?: string | null; // ID of the route owner/creator
  currentUserId?: string | null; // ID of the current logged-in user
  onRouteRendered?: () => void; // Callback when route is fully rendered
  highlightedSurface?: string | null; // Surface type to highlight
  highlightedSegmentIndex?: number; // Index of the highlighted segment
  highlightedClimbIndex?: number | null; // Index of the highlighted climb
  routeSegments?: any[]; // Route segments for surface highlighting
  elevationData?: any[]; // Elevation data for climb highlighting
  onMapStateChange?: (center: Position, zoom: number) => void; // Callback for map center and zoom changes
  initialCenter?: Position; // Initial map center position
  initialZoom?: number; // Initial map zoom level
  targetCenter?: Position; // Target center for programmatic view updates
  targetZoom?: number; // Target zoom for programmatic view updates
  shouldUpdateMapView?: boolean; // Whether to update the map view to target position
  routeData?: any; // Full route data for metrics summary
  routeProfile?: string; // Current route profile key for bike display
}

// Icons für Markierungen
const startIcon = L.icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="52">
      <path fill="#4CAF50" d="M16 2 C10 2 4 7 4 15 4 23 16 30 16 30 16 30 28 23 28 15 28 7 22 2 16 2 Z"/>
      <circle fill="white" cx="16" cy="15" r="6"/>
    </svg>
  `),
  iconSize: [32, 52],
  iconAnchor: [16, 48], // Center horizontally, position tip at GPS coordinate
  popupAnchor: [1, -44]
});

const endIcon = L.icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="52">
      <path fill="#f44336" d="M16 2 C10 2 4 7 4 15 4 23 16 30 16 30 16 30 28 23 28 15 28 7 22 2 16 2 Z"/>
      <circle fill="white" cx="16" cy="15" r="6"/>
    </svg>
  `),
  iconSize: [32, 52],
  iconAnchor: [16, 48], // Center horizontally, position tip at GPS coordinate
  popupAnchor: [1, -44]
});

// Function to create numbered via point icons
const createNumberedViaIcon = (number: number) => {
  // Determine circle size and font size based on number of digits
  const digits = number.toString().length;
  const circleRadius = digits === 1 ? 7 : digits === 2 ? 8 : 10; // Smaller for 1-2 digits, original size for 3+ digits
  const fontSize = digits === 1 ? 9 : digits === 2 ? 9 : 10; // Slightly smaller font for 1-2 digits
  
  // Thinner marker shape to match start/end markers better
  const svgString = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="52"><path fill="#2196f3" d="M16 2 C10 2 4 7 4 15 4 23 16 30 16 30 16 30 28 23 28 15 28 7 22 2 16 2 Z"/><circle fill="white" cx="16" cy="15" r="${circleRadius}"/><text x="16" y="19" text-anchor="middle" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="bold" fill="#2196f3">${number}</text></svg>`;
  
  return L.icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(svgString),
    iconSize: [32, 52],
    iconAnchor: [16, 48], // Center horizontally, position tip at GPS coordinate
    popupAnchor: [1, -44]
  });
};

interface ContextMenuState {
  mouseX: number;
  mouseY: number;
  latlng: L.LatLng | null;
}

export const CyclingMap: React.FC<CyclingMapProps> = ({
  points,
  route,
  isLoading,
  hasRoutingError = false,
  isSavingRoute = false,
  onMapClick,
  onMarkerClick,
  onMarkerDrag,
  activePoint,
  onHover,
  detailPanelOpen,
  onFitMapToRoute,
  onAddRoutePointViaSearch,
  onManualFitToRoute,
  onDeleteRoute,
  onUndo,
  onRedo,
  onReverseRoute,
  onDownload,
  onGpxDownload,
  onResetRoute,
  onOverwriteSave,
  canUndo = false,
  canRedo = false,
  hasRoutePoints = false,
  routePointsCount = 0,
  hasLoadedRoute = false,
  hasUnsavedChanges = false,
  currentRouteOwnerId = null,
  currentUserId = null,
  onRouteRendered,
  highlightedSurface,
  highlightedSegmentIndex,
  highlightedClimbIndex,
  routeSegments,
  elevationData,
  onMapStateChange,
  initialCenter,
  initialZoom,
  targetCenter,
  targetZoom,
  shouldUpdateMapView
  ,routeData,
  routeProfile
}) => {
  const mapRef = useRef<L.Map | null>(null);
  const viewEditModeToggleRef = useRef<ViewEditModeToggleRef>(null);
  const [snapPoint, setSnapPoint] = useState<Position | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [isMapShifted, setIsMapShifted] = useState(false);
  const [userLocation, setUserLocation] = useState<L.LatLng | null>(null);
  const [viewEditMode, setViewEditMode] = useState<ViewEditMode>('edit'); // Default to edit mode
  const initializedRef = useRef(false);

  // Neuer useEffect für die Synchronisation von activePoint und snapPoint
  useEffect(() => {
    if (activePoint !== null && route && route.length > activePoint) {
      // Finde den genauen Punkt auf dem Routensegment
      if (activePoint < route.length - 1) {
        const start = route[activePoint];
        const end = route[activePoint + 1];
        // Interpoliere einen Punkt in der Mitte des Segments
        const interpolatedPoint = {
          lat: (start.lat + end.lat) / 2,
          lng: (start.lng + end.lng) / 2
        };
        setSnapPoint(interpolatedPoint);
      } else {
        // Wenn es der letzte Punkt ist
        setSnapPoint(route[activePoint]);
      }
    } else {
      setSnapPoint(null);
    }
  }, [activePoint, route]);

  // Effect to shift map center when detail panel opens/closes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Calculate detail panel height based on screen size
    const detailPanelHeight = getDetailPanelHeightForMapShift();
    
    // Get current center
    const currentCenter = map.getCenter();

    if (!initializedRef.current) {
      initializedRef.current = true;
      if (detailPanelOpen) {
        setIsMapShifted(true);
      }
      return;
    }
    
    if (detailPanelOpen && !isMapShifted) {
      // Shift center south (down) when panel opens
      const pixelShift = detailPanelHeight / 2;
      const mapSize = map.getSize();
      const latShift = (pixelShift / mapSize.y) * (map.getBounds().getNorth() - map.getBounds().getSouth());
      
      map.setView([currentCenter.lat - latShift, currentCenter.lng], map.getZoom(), {
        animate: true,
        duration: 0.3
      });
      setIsMapShifted(true);
    } else if (!detailPanelOpen && isMapShifted) {
      // Shift center north (up) when panel closes
      const pixelShift = detailPanelHeight / 2;
      const mapSize = map.getSize();
      const latShift = (pixelShift / mapSize.y) * (map.getBounds().getNorth() - map.getBounds().getSouth());
      
      map.setView([currentCenter.lat + latShift, currentCenter.lng], map.getZoom(), {
        animate: true,
        duration: 0.3
      });
      setIsMapShifted(false);
    }
  }, [detailPanelOpen, isMapShifted]);

  const handleContextMenu = (e: L.LeafletMouseEvent) => {
    // Don't show context menu in view mode, but highlight the "View Only" indicator
    if (viewEditMode === 'view') {
      viewEditModeToggleRef.current?.highlightViewOnly();
      return;
    }
    
    e.originalEvent.preventDefault();
    
    // Trigger haptic feedback when context menu opens
    hapticMedium();
    
    // Position menu so bottom-right corner is at touch point
    // No offset needed - transformOrigin will handle positioning from bottom-right
    setContextMenu({
      mouseX: e.originalEvent.clientX,
      mouseY: e.originalEvent.clientY,
      latlng: e.latlng
    });
  };

  const handleMenuItemClick = (type: 'end' | 'via') => {
    if (contextMenu?.latlng) {
      // Trigger light haptic feedback on menu item selection
      hapticMedium();
      
      onMapClick({ lat: contextMenu.latlng.lat, lng: contextMenu.latlng.lng }, type, false); // false = not from search
      setContextMenu(null);
    }
  };

  const handleClose = () => {
    setContextMenu(null);
  };

  const handleAddRoutePointViaSearch = (position: { lat: number; lon: number }, pointType?: 'via' | 'end') => {
    if (onAddRoutePointViaSearch) {
      onAddRoutePointViaSearch({ lat: position.lat, lng: position.lon }, pointType);
    }
  };

  // Handler for view/edit mode changes
  const handleModeChange = (mode: ViewEditMode) => {
    setViewEditMode(mode);
  };

  // Custom map click handler that triggers highlight in view mode
  const handleMapClick = (position: Position, pointType: 'via' | 'end' = 'end') => {
    // In view mode, highlight the "View Only" indicator when user tries to click
    if (viewEditMode === 'view') {
      viewEditModeToggleRef.current?.highlightViewOnly();
      return; // Don't call onMapClick to prevent route point addition
    }
    
    // Only call the original onMapClick in edit mode
    onMapClick(position, pointType);
  };

  return (
    <div className={`map-container${isLoading ? ' loading' : ''}${isSavingRoute ? ' saving' : ''}`}>
      {isLoading && (
        <div className="loading-overlay">
          <CircularProgress style={{ color: '#1976d2' }} size={40} />
        </div>
      )}
      {isSavingRoute && (
        <div className="saving-overlay">
          <div className="saving-content">
            <CircularProgress className="saving-spinner" size={40} />
            <div className="saving-text">
              <div className="saving-title">Route wird gespeichert...</div>
              <div className="saving-subtitle">Thumbnail wird generiert</div>
            </div>
          </div>
        </div>
      )}
      <div className="map-attribution-safety-zone">
        <div className="map-attribution">
          <span><a href="https://brouter.de" target="_blank" rel="noopener noreferrer">BRouter</a> powered by</span>
          <span>&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors</span>
        </div>
      </div>
      {!detailPanelOpen && hasRoutePoints && (
        <div className="map-disclaimer-safety-zone">
          <div className="map-disclaimer-overlay">
            <RouterLink to="/ride-responsibly" className="map-disclaimer-link">
              <span className="map-disclaimer-text">
                <span>Before You Go</span>
                <span>check <span className="map-disclaimer-emphasis">Ride Responsibly</span> <InfoOutlinedIcon sx={{ fontSize: 10 }} /></span>
              </span>
            </RouterLink>
          </div>
        </div>
      )}
      <div className="map-inner-container">
        <MapContainer
          center={initialCenter ? [initialCenter.lat, initialCenter.lng] : [50.118179, 8.654897]}
          zoom={initialZoom}
          className="map"
          ref={mapRef}
          attributionControl={false}
          preferCanvas={true}
          // Optimize rendering for better zoom performance
          zoomAnimation={true}
          fadeAnimation={true}
          markerZoomAnimation={true}
          // Reduce transform3d issues during zoom
          worldCopyJump={false}
          // Ultra-smooth zoom handling - disable all zoom snapping for true continuous zoom
          zoomSnap={0} // Disable zoom snapping completely for continuous zoom
          zoomDelta={0.01} // Very small increments
          wheelPxPerZoomLevel={200} // Require more wheel movement for precise control
          // Mobile-specific optimizations
          touchZoom={true}
          bounceAtZoomLimits={false}
          // Improve touch responsiveness
          zoomControl={false}
          doubleClickZoom={false} // Disabled - we use custom adaptive zoom
          // Reduce lag on mobile devices
          trackResize={true}
          // Enhanced mobile settings
          zoomAnimationThreshold={4}
          // Prevent zoom beyond reasonable limits
          maxZoom={18}
          minZoom={6}
          // Better mobile interaction handling
          keyboard={false}
          scrollWheelZoom={true}
          dragging={true}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <AdaptiveRouteLayer 
            route={route}
            onHover={onHover}
            setSnapPoint={setSnapPoint}
            onRouteRendered={onRouteRendered}
            highlightedSurface={highlightedSurface}
            routeSegments={routeSegments || []}
          />
          
          {/* Surface highlighting overlay */}
          <SurfaceHighlightLayer 
            route={route}
            highlightedSurface={highlightedSurface || null}
            highlightedSegmentIndex={highlightedSegmentIndex}
            routeSegments={routeSegments || []}
          />
          
          {/* Climb highlighting overlay */}
          <ClimbHighlightLayer 
            route={route}
            elevationData={elevationData || []}
            highlightedClimbIndex={highlightedClimbIndex ?? null}
            climbs={routeData?.climbs}
          />
          
          {points.map((point, index) => {
            if (!point.position) return null;
            
            // Calculate via point number (excluding start point)
            let viaNumber = 0;
            if (point.type === 'via') {
              // Count via points that come before this point
              viaNumber = points.slice(0, index).filter(p => p.type === 'via').length + 1;
            }
            
            // Determine icon based on point type
            let icon;
            if (point.type === 'start') {
              icon = startIcon;
            } else if (point.type === 'end') {
              icon = endIcon;
            } else {
              icon = createNumberedViaIcon(viaNumber);
            }
            
            return (
                            <Marker
                key={point.id}
                position={point.position}
                icon={icon}
                draggable={viewEditMode === 'edit'} // Only allow dragging in edit mode
                eventHandlers={{
                  click: (e) => {
                    e.originalEvent.stopPropagation();
                    onMarkerClick(point.id);
                  },
                  dragstart: () => {
                    setSnapPoint(null);
                    onHover(null);
                  },
                  dragend: (e) => {
                    const marker = e.target;
                    const position = marker.getLatLng();
                    onMarkerDrag(point.id, { lat: position.lat, lng: position.lng });
                  }
                }}
              />
            );
          })}
          
          {/* Hover-Punkt */}
          {snapPoint && (
            <Marker
              key="hover-marker"
              position={snapPoint}
              icon={L.divIcon({
                className: 'snap-point-marker',
                iconSize: [24, 24],
                iconAnchor: [12, 12]
              })}
              interactive={false}
            />
          )}
          
          {/* GPS-Position des Benutzers */}
          {userLocation && (
            <Marker
              key="user-location-marker"
              position={[userLocation.lat, userLocation.lng]}
              icon={L.divIcon({
                className: 'user-location-dot',
                iconSize: [20, 20],
                iconAnchor: [10, 10]
              })}
              interactive={false}
            />
          )}
          
          {/* Temporary marker for context menu */}
          {contextMenu?.latlng && (
            <Marker
              key="temp-context-marker"
              position={[contextMenu.latlng.lat, contextMenu.latlng.lng]}
              icon={L.icon({
                iconUrl: 'data:image/svg+xml;base64,' + btoa(`
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="25" height="41">
                    <defs>
                      <linearGradient id="splitGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="50%" stop-color="#2196f3"/>
                        <stop offset="50%" stop-color="#f44336"/>
                      </linearGradient>
                    </defs>
                    <path fill="url(#splitGradient)" d="M16 2 C10 2 4 7 4 15 4 23 16 30 16 30 16 30 28 23 28 15 28 7 22 2 16 2 Z"/>
                    <circle fill="white" cx="16" cy="15" r="6"/>
                  </svg>
                `),
                iconSize: [25, 41],
                iconAnchor: [12.5, 38], // Center horizontally, position tip at GPS coordinate
                popupAnchor: [1, -34]
              })}
              interactive={false}
            />
          )}
          
          <MapEvents 
            onMapClick={handleMapClick} 
            onContextMenu={handleContextMenu} 
            onCloseContextMenu={handleClose}
            viewEditMode={viewEditMode}
          />
          
          {/* Track map state changes for session storage */}
          {onMapStateChange && (
            <MapViewController 
              onMapStateChange={onMapStateChange}
              targetCenter={targetCenter}
              targetZoom={targetZoom}
              shouldUpdateView={shouldUpdateMapView}
            />
          )}
        </MapContainer>

        {/* Map Controls außerhalb des MapContainers */}
        <MapControls 
          mapRef={mapRef} 
          onUserLocationChange={setUserLocation}
          onAddRoutePointViaSearch={handleAddRoutePointViaSearch}
          routePointsCount={points.length}
          onFitMapToRoute={onFitMapToRoute}
          detailPanelOpen={detailPanelOpen}
        />

        <MapControlsRoute
          hasRoutePoints={hasRoutePoints}
          routePointsCount={routePointsCount}
          isSavingRoute={isSavingRoute}
          hasRoutingError={hasRoutingError}
          onFitToRoute={() => {
            onManualFitToRoute && onManualFitToRoute();
          }}
          onDeleteRoute={() => {
            onDeleteRoute && onDeleteRoute();
          }}
          onUndo={() => {
            onUndo && onUndo();
          }}
          onRedo={() => {
            onRedo && onRedo();
          }}
          onReverseRoute={() => {
            onReverseRoute && onReverseRoute();
          }}
          onDownload={() => {
            onDownload && onDownload();
          }}
          onGpxDownload={() => {
            onGpxDownload && onGpxDownload();
          }}
          onResetRoute={() => {
            onResetRoute && onResetRoute();
          }}
          onOverwriteSave={() => {
            onOverwriteSave && onOverwriteSave();
          }}
          canUndo={canUndo}
          canRedo={canRedo}
          hasLoadedRoute={hasLoadedRoute || false}
          hasUnsavedChanges={hasUnsavedChanges}
          currentRouteOwnerId={currentRouteOwnerId}
          currentUserId={currentUserId}
          mapRef={mapRef}
          routeData={routeData}
          bikeProfileName={routeProfile}
        />

        {/* View/Edit Mode Toggle */}
        <ViewEditModeToggle
          ref={viewEditModeToggleRef}
          mode={viewEditMode}
          onModeChange={handleModeChange}
          disabled={isLoading}
        />

        <Menu
          open={contextMenu !== null}
          onClose={handleClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={() => handleMenuItemClick('end')} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: '#f44336',
              border: '2px solid #f44336',
              color: 'white',
              fontSize: '10px',
              fontWeight: '600',
              fontFamily: 'monospace'
            }}>
              E
            </div>
            Add End Point
          </MenuItem>
          <MenuItem onClick={() => handleMenuItemClick('via')} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: '#2196F3',
              border: '2px solid #2196F3',
              color: 'white',
              fontSize: '10px',
              fontWeight: '600',
              fontFamily: 'monospace'
            }}>
              +
            </div>
            Add Via Point
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
};
